import { useState, useEffect } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export default function TimesheetDailyModal({ 
  isOpen, 
  onClose, 
  monthKey,
  initialEntries,
  onSave, 
  isLocked = false
}) {
  const [entries, setEntries] = useState({});

  useEffect(() => {
    if (isOpen) {
      setEntries(initialEntries || {});
    }
  }, [isOpen, initialEntries]);

  if (!monthKey) return null;

  // Generate days avoiding timezone shifts by using simple string parsing if needed
  // parseISO on YYYY-MM-01 gives local midnight, which is mostly fine.
  const monthDate = parseISO(`${monthKey}-01`);
  const start = startOfMonth(monthDate);
  const end = endOfMonth(monthDate);
  const days = eachDayOfInterval({ start, end }).filter(day => !isWeekend(day));

  const handleDayChange = (dateStr, value) => {
    if (isLocked) return;
    setEntries(prev => {
      const next = { ...prev };
      if (value === "" || value === null) {
        delete next[dateStr];
      } else {
        next[dateStr] = Number(value);
      }
      return next;
    });
  };

  const handleSave = () => {
    // If entries is totally empty but user explicitly cleared it, we still want to save the empty record
    // We can just pass the record up
    onSave({ ...entries });
    onClose();
  };

  const handleClear = () => {
    setEntries({});
  };

  const total = Object.values(entries).reduce((sum, val) => sum + (Number(val) || 0), 0);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px] border-white/10 glass-heavy text-white">
        <DialogHeader>
          <DialogTitle>Détail des jours travaillés</DialogTitle>
          <DialogDescription className="text-slate-400 capitalize">
            {format(monthDate, "MMMM yyyy", { locale: fr })} — Total: {total}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto pr-3 space-y-2 mt-2">
          {days.map(day => {
            const dateStr = format(day, "yyyy-MM-dd");
            const val = entries[dateStr] !== undefined ? entries[dateStr] : "";
            
            return (
              <div key={dateStr} className="flex items-center justify-between text-sm py-1 border-b border-white/5 last:border-0">
                <span className="capitalize text-slate-200">{format(day, "EEEE dd", { locale: fr })}</span>
                <select
                  value={val}
                  onChange={(e) => handleDayChange(dateStr, e.target.value)}
                  disabled={isLocked}
                  className="w-20 bg-slate-950 border border-white/10 rounded px-2 py-1.5 focus:ring-1 focus:ring-lenvia-blue disabled:opacity-50 text-white"
                >
                  <option value="">-</option>
                  <option value="0">0</option>
                  <option value="0.5">0.5</option>
                  <option value="1">1</option>
                </select>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between mt-6">
          {!isLocked ? (
              <Button type="button" variant="ghost" onClick={handleClear} className="text-red-400 hover:text-red-300 hover:bg-red-400/10 h-9 px-3">
                Effacer tout
              </Button>
          ) : <div />}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-white/10 text-white hover:bg-white/5 h-9">
              Fermer
            </Button>
            {!isLocked && (
              <Button type="button" onClick={handleSave} className="bg-lenvia-blue hover:bg-lenvia-blue/90 text-white h-9">
                Valider ({total})
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
